import { API_ENDPOINT } from "@/constants/appConstants";
import { RECOMMENDED_JOBS_QUERY_KEY } from "@/lib/queryKeys";
import { getJDUrlWithDomain } from "@/utils/jobs";
import {
  Button,
  CheckboxList,
  MessageCard,
  Toggle,
  Tooltip,
} from "@monsterindia/global-ui-components";
import { JobCard } from "@monsterindia/seeker-jobs";
import { useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useRecommendedPageData } from "../";
import { useDashboardContext } from "../..";
import { getAdjustedHeight } from "../../utils/getDashboardAdjustedHeight";
import { jobSaveUnsaveAPI } from "../jobDescription/query";
import { useRecommendedJobs } from "../query";
import {
  DashboardContextProps,
  RecommendedJobAPIDataType,
  RecommendedJobAPIKeysType,
  RecommendedPageContextProps,
  RecommendedPageDataType,
  SelectedFilter,
  checkBoxInputType,
  selectAllEventType,
} from "../types";
import CareerServiceBanners from "./components/careerServiceBanners";
import "./components/styles.css";
import Skeleton from "./skeleton";

const ApplyJob = dynamic<{
  jobData: RecommendedJobAPIDataType;
  siteProperties: sitePropsType | undefined;
  pageName: string;
  btnClass: string;
  isLoggedIn: boolean;
  isSyndicateQuickApplyEnabled: boolean;
  isMobile: boolean;
  applyBtnType?: string;
  applyBtnConfigure?: {
    fetch?: boolean;
    selected?: boolean;
    reset?: boolean;
    isJobApplied?: boolean;
  };
  isJobApplied?: boolean;
  doNotOpenConsecutiveApops?: boolean;
  inputHandler?: (data: checkBoxInputType) => void;
  inputResponse?: (data: any) => void;
  inputReset?: (data: any) => void;
  inputSelection: boolean;
  bulkApplyBtnData?: {
    label: string;
    modifier: string;
    url: string;
    className: string;
    size: string;
    jobIds: Array<number>;
  };
  divClass?: string;
  originPage?: string;
}>(() => import("@monsterindia/seeker-jobs").then((mod) => mod.ApplyJob), {
  ssr: false,
});

const RecommendedJobCard = () => {
  const { isMobile, host }: DashboardContextProps = useDashboardContext();
  const {
    recommendedJobData,
    setRecommendedJobData,
    handleEventTracking,
    jobUserInfoHandler,
    cleverTapPropertyValues,
  }: RecommendedPageContextProps = useRecommendedPageData();
  const [recommendedJobParams, setRecommendedJobParams] = useState({
    initialPage: 2,
    currentPage: 1,
    filterStatus: recommendedJobData.selectedFilter?.[0]?.id || "",
    isFetchingEnabled: false,
    resetType: { value: "none" },
    loadingJobCard: true,
    quickApplyFilter: false,
    lastDataLength: recommendedJobData.totalDataLength,
  });
  const [inputMultiSelectExists, setInputMultiSelectExists] = useState<
    boolean | null
  >(null);
  const [enableSelectAll, setEnableSelectAll] = useState(false);
  const [toggleDashboardHeader, setToggleDashboardHeader] = useState(false);
  const [queryParams, setQueryParams] = useState<{
    jobIds?: number[];
    action?: string | undefined;
  }>({
    jobIds: [],
    action: undefined,
  });
  const tooltipBtnRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const multiSelectBtnRef = useRef<HTMLDivElement | null>(null);
  const selectAllTextRef = useRef<HTMLDivElement | null>(null);
  const bulkApplyBtnContainerRef = useRef<HTMLDivElement | null>(null);
  const mediaUrl =
    recommendedJobData.siteProps?.media_url || "media.foundit.in";
  const isSyndicateQuickApplyEnabled =
    recommendedJobData?.windowData?.isSyndicateQuickApplyEnabled || false;
  const showPagingLength = recommendedJobData.stopFilterCalls
    ? recommendedJobData.totalPagingLength
    : recommendedJobData.totalPagingLength +
      (recommendedJobData?.data?.["appliedHistory"]?.data?.length || 0);

  const bulkApplyMultiSelect =
    recommendedJobData?.windowData?.bulkApplyMultiSelect || "";

  const multiSelectFeature = true;

  const queryClient = useQueryClient();

  const {
    data,
    isError,
    isFetchingNextPage,
    isFetching,
    refetch,
    fetchNextPage,
  } = useRecommendedJobs(
    recommendedJobParams.filterStatus,
    recommendedJobParams.isFetchingEnabled,
    recommendedJobParams.initialPage,
    recommendedJobParams.quickApplyFilter,
  );

  const cardClickHandler = (url: string, index: number) => {
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      return {
        ...prevData,
        isLoadingJobDesc: true,
        jobDescIndex: { selectedIndex: index },
      };
    });
    handleEventTracking({
      eventName: "Job Card Clicked",
      eventType: "eventPush",
      eventProps: {
        page_name:
          cleverTapPropertyValues[
            recommendedJobData.activeTab as keyof typeof cleverTapPropertyValues
          ],
      },
    });
    window.open(url, isMobile ? "_self" : "_blank");
  };

  const saveUnsaveJobHandler = async (
    jobId: number,
    type: string,
    props: { callBackSuccess: boolean },
  ) => {
    let response: any;
    let responseMessage;
    if (type === "apiCall") {
      response = await jobSaveUnsaveAPI(
        jobId,
        recommendedJobData.savedJobs.includes(jobId) ? "unsave" : "save",
      );
      responseMessage = response?.successMessage;
    } else if (type == "callBack") {
      if (props.callBackSuccess) {
        responseMessage = "Job saved successfully";
      } else {
        responseMessage = "Job unsaved successfully";
      }
    }

    if (responseMessage == "Job saved successfully") {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
          savedJobs: [...(prevData?.savedJobs || []), jobId],
        };
        if (type === "apiCall")
          savedJobsModified["toastMessage"] = {
            text: "Job saved successfully",
            type: "success",
            show: true,
          };
        if (!prevData.saveUnsaveJobHandler)
          savedJobsModified["saveUnsaveJobHandler"] = saveUnsaveJobHandler;
        return savedJobsModified;
      });
    } else if (responseMessage == "Job unsaved successfully") {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const newSavedJobs =
          prevData?.savedJobs?.filter(
            (prevJobId: number) => prevJobId !== jobId,
          ) || [];
        const savedJobsModified = {
          ...prevData,
          savedJobs: [...newSavedJobs],
        };
        if (type === "apiCall")
          savedJobsModified["toastMessage"] = {
            text: "Job unsaved successfully",
            type: "success",
            show: true,
          };
        if (!prevData.saveUnsaveJobHandler)
          savedJobsModified["saveUnsaveJobHandler"] = saveUnsaveJobHandler;
        return savedJobsModified;
      });
    } else {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
        };
        if (type === "apiCall")
          savedJobsModified["toastMessage"] = {
            text: "Job saving unsuccessful",
            type: "error",
            show: true,
          };
        return savedJobsModified;
      });
      console.log("Error in saving/unsaving job");
    }
  };

  const mergeAppended = (
    prevObject: RecommendedJobAPIKeysType,
    newObject: RecommendedJobAPIKeysType,
    combinedData: RecommendedJobAPIDataType[],
  ) => {
    const categories = Object.keys(prevObject);

    const appended = categories.flatMap((key) => {
      const oldArr =
        prevObject[key as keyof RecommendedJobAPIKeysType]?.data || [];
      const newArr =
        newObject[key as keyof RecommendedJobAPIKeysType]?.data || [];
      return newArr?.slice(oldArr.length);
    });

    return [...combinedData, ...appended];
  };

  const handlePaginationScroll = () => {
    const scrollRef = !isError
      ? itemRefs.current?.[recommendedJobParams.lastDataLength - 1]
      : itemRefs.current?.[recommendedJobParams.lastDataLength - 3];
    if (scrollRef) {
      scrollRef?.scrollIntoView();
    }
  };

  const handleScroll = useCallback(
    async (event: React.UIEvent<HTMLDivElement> | Event) => {
      if (isFetchingNextPage || isFetching) return;

      const { scrollTop, scrollHeight, clientHeight } = !isMobile
        ? (event.currentTarget as HTMLDivElement)
        : ((event.target as Document).scrollingElement as HTMLElement);
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (
          recommendedJobData.totalDataLength >=
            recommendedJobData.totalPagingLength ||
          recommendedJobData.isLoadingJobDesc
        ) {
          if (recommendedJobParams.filterStatus === "preference") {
            setRecommendedJobData((prev: RecommendedPageDataType) => {
              let recommendedJobsModified = {
                ...prev,
                nextFilter: true,
              };
              return recommendedJobsModified;
            });
          }
          return;
        }

        setRecommendedJobParams((prev) => ({
          ...prev,
          currentPage: prev.currentPage + 1,
          loadingJobCard: true,
          lastDataLength: recommendedJobData.totalDataLength,
        }));
        fetchNextPage();
      }
    },
    [
      isFetchingNextPage,
      isFetching,
      fetchNextPage,
      recommendedJobParams,
      recommendedJobData,
    ],
  );

  const resetAndRefetch = () => {
    queryClient.removeQueries({
      queryKey: [
        RECOMMENDED_JOBS_QUERY_KEY,
        recommendedJobParams.filterStatus,
        recommendedJobParams.initialPage,
        recommendedJobParams.quickApplyFilter,
      ],
      exact: true,
    });
    setRecommendedJobParams((prev) => {
      return {
        ...prev,
        initialPage: 1,
        currentPage: 1,
        filterStatus: recommendedJobData.selectedFilter?.[0]?.id || "",
        quickApplyFilter: !!recommendedJobData.quickApplyFilter,
        resetType: { value: "filterClick" },
        loadingJobCard: true,
        lastDataLength: recommendedJobData.totalDataLength,
      };
    });
  };

  const adjustHeight = useCallback(() => {
    if (containerRef.current) {
      const remainingHeight = getAdjustedHeight("recommended");
      containerRef.current.style.height = `${remainingHeight}px`;
    }
  }, [containerRef.current, selectAllRef.current, isMobile]);

  const updateJobApplicationState = (
    jobId: string,
    isApplied: boolean,
    indexVal: number | null,
  ) => {
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      const responseData = { ...prevData };
      const jobIndex = !indexVal
        ? responseData.displayRecommendedData?.findIndex(
            (job: RecommendedJobAPIDataType) => job?.id === jobId,
          )
        : indexVal;

      if (jobIndex >= 0 && Array.isArray(responseData.displayRecommendedData)) {
        responseData.displayRecommendedData[jobIndex] = {
          ...responseData?.displayRecommendedData?.[jobIndex],
          isApplied: true,
        };

        window?.dataLayer?.push({
          event: "Apply_Success",
          CriteoJobID:
            responseData?.displayRecommendedData?.[jobIndex]?.kiwiJobId,
        });
      } else {
        console.error("Job not found in search results:", jobId);
      }
      return responseData;
    });
  };

  const onHandleQuickApplyFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (recommendedJobData.multiSelectCount > 0) {
      setRecommendedJobData((prev: RecommendedPageDataType) => {
        let newData = {
          ...prev,
          discardBulkApplyPopup: {
            value: true,
            type: e?.target?.checked ? "quickApplySelect" : "quickApplyRemove",
            decision: null,
            argumentStorage: e?.target?.checked || false,
          },
        };
        return newData;
      });
    } else {
      setRecommendedJobData((prev: RecommendedPageDataType) => {
        let newData = {
          ...prev,
          quickApplyFilter: e?.target?.checked || false,
          filterDisable: true,
          isLoadingJobDesc: true,
          selectFilterFetch: "client",
        };
        return newData;
      });
    }
  };

  const handleQuickApplyFilter = (
    selectedOption: SelectedFilter,
    type: string,
  ) => {
    setRecommendedJobData((prev: RecommendedPageDataType) => {
      let newData = {
        ...prev,
        quickApplyFilter: selectedOption,
        filterDisable: true,
        isLoadingJobDesc: true,
        selectFilterFetch: "client",
      };
      return newData;
    });
    handleEventTracking({
      eventName: "Pageviewed",
      eventType: "eventPush",
      eventProps: {
        page_name: `${cleverTapPropertyValues[recommendedJobData.activeTab as keyof typeof cleverTapPropertyValues]}${type == "remove" ? "quickApplyFilterRemove" : "quickApplyFilterApply"}`,
      },
    });
  };

  const eventChangeHandlerForSelectAll = (data: selectAllEventType) => {
    const newCheckValue = data.values && data.values?.length > 0;
    if (localStorage.getItem("multiSelectJobsFirstVisit") !== "true") {
      tooltipBtnRef?.current?.click();
      handleToolTipStorage();
    }
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      const savedJobsModified = {
        ...prevData,
      };

      if (!savedJobsModified?.clearAllMultiSelectSelected) {
        savedJobsModified?.firstMultiSelectIndex?.forEach((key: number) => {
          if (savedJobsModified?.displayRecommendedData) {
            savedJobsModified.displayRecommendedData[key] = {
              ...savedJobsModified.displayRecommendedData[key],
              multiSelectData: {
                ...savedJobsModified.displayRecommendedData[key]
                  ?.multiSelectData,
                configureData: {
                  ...savedJobsModified?.displayRecommendedData?.[key]
                    ?.multiSelectData?.configureData,
                  reset: true,
                },
              },
            };
          }
        });
        savedJobsModified.doNotOpenConsecutiveApops = false;
        savedJobsModified.multiSelectCount = 0;
        savedJobsModified.firstMultiSelectIndex = [];
        savedJobsModified.multiSelectJobIds = [];
        savedJobsModified.totalSuccessfulBulkApplyData = undefined;
      }
      return savedJobsModified;
    });

    setTimeout(() => {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
        };

        if (!savedJobsModified?.clearAllMultiSelectSelected) {
          savedJobsModified.allMultiSelectSelected = newCheckValue;
          savedJobsModified.retainAllMultiSelectSelected = newCheckValue;
          setEnableSelectAll(false);
        } else {
          if (
            (recommendedJobParams.currentPage !== 1 ||
              recommendedJobData.stopFilterCalls ||
              recommendedJobData.nextFilter) &&
            savedJobsModified.triggeredActionSource !== "clearSelection" &&
            savedJobsModified.triggeredActionSource !== "applySuccess" &&
            savedJobsModified.retainAllMultiSelectSelected
          ) {
            setTimeout(() => {
              setEnableSelectAll(true);
            }, 500);
          } else {
            setEnableSelectAll(false);
          }
          savedJobsModified.clearAllMultiSelectSelected = false;
          savedJobsModified.allMultiSelectSelected = false;
          savedJobsModified.retainAllMultiSelectSelected = false;
          savedJobsModified.triggeredActionSource = undefined;
          savedJobsModified.resetQuickApplyFilter = false;
        }
        return savedJobsModified;
      });
    }, 100);
  };

  const bulkApplyInputHandler = (
    checkBoxData: checkBoxInputType,
    index: number,
    data: RecommendedJobAPIDataType,
  ) => {
    const tempMultiSelectCount =
      bulkApplyMultiSelect && typeof bulkApplyMultiSelect === "string"
        ? parseInt(bulkApplyMultiSelect)
        : 5;
    if (!checkBoxData.reset) {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        let modifiedData = {
          ...prevData,
        };
        if (modifiedData.multiSelectCount < tempMultiSelectCount) {
          modifiedData.firstMultiSelectIndex = [
            ...(modifiedData.firstMultiSelectIndex || []),
          ];
          modifiedData.multiSelectJobIds = [
            ...(modifiedData.multiSelectJobIds || []),
          ];
          if (checkBoxData.selected) {
            modifiedData.firstMultiSelectIndex.push(index);
            if (checkBoxData?.id) {
              modifiedData.multiSelectJobIds.push(checkBoxData?.id);
            }
          } else if (
            !checkBoxData.selected &&
            modifiedData.firstMultiSelectIndex.includes(index)
          ) {
            modifiedData.firstMultiSelectIndex =
              modifiedData.firstMultiSelectIndex.filter(
                (item: number) => item !== index,
              );
            modifiedData.multiSelectJobIds =
              modifiedData.multiSelectJobIds.filter(
                (item: number) => item != data?.jobId,
              );
          }

          modifiedData.multiSelectCount =
            modifiedData.firstMultiSelectIndex.length;

          if (
            modifiedData.displayRecommendedData &&
            modifiedData.displayRecommendedData[index]
          ) {
            modifiedData.displayRecommendedData[index] = {
              ...modifiedData.displayRecommendedData[index],
              multiSelectData: {
                ...modifiedData.displayRecommendedData[index]?.multiSelectData,
                configureData: {
                  ...modifiedData?.displayRecommendedData?.[index]
                    ?.multiSelectData?.configureData,
                  selected: checkBoxData?.selected || false,
                  reset: false,
                },
              },
            };
          }
          return modifiedData;
        } else if (
          modifiedData.multiSelectCount >= tempMultiSelectCount &&
          checkBoxData.selected
        ) {
          if (!modifiedData.allMultiSelectSelected) {
            modifiedData["toastMessage"] = {
              text: `You cannot select more than ${tempMultiSelectCount} jobs at a time.`,
              type: "error",
              show: true,
            };
          }
          if (
            modifiedData.displayRecommendedData &&
            modifiedData.displayRecommendedData[index]
          ) {
            modifiedData.displayRecommendedData[index] = {
              ...modifiedData.displayRecommendedData[index],
              multiSelectData: {
                ...modifiedData.displayRecommendedData[index]?.multiSelectData,
                configureData: {
                  ...modifiedData?.displayRecommendedData?.[index]
                    ?.multiSelectData?.configureData,
                  selected: false,
                  reset: true,
                },
              },
            };
          }
          modifiedData.multiSelectCount = tempMultiSelectCount;
        } else if (
          modifiedData.multiSelectCount >= tempMultiSelectCount &&
          !checkBoxData.selected
        ) {
          modifiedData.firstMultiSelectIndex = [
            ...(modifiedData.firstMultiSelectIndex || []),
          ];
          modifiedData.multiSelectJobIds = [
            ...(modifiedData.multiSelectJobIds || []),
          ];
          if (modifiedData.firstMultiSelectIndex.includes(index)) {
            modifiedData.firstMultiSelectIndex =
              modifiedData.firstMultiSelectIndex.filter(
                (item: number) => item !== index,
              );

            modifiedData.multiSelectJobIds =
              modifiedData.multiSelectJobIds.filter(
                (item: number) => item != data?.jobId,
              );
          }

          modifiedData.multiSelectCount =
            modifiedData.firstMultiSelectIndex.length;

          if (
            modifiedData.displayRecommendedData &&
            modifiedData.displayRecommendedData[index]
          ) {
            modifiedData.displayRecommendedData[index] = {
              ...modifiedData.displayRecommendedData[index],
              multiSelectData: {
                ...modifiedData.displayRecommendedData[index]?.multiSelectData,
                configureData: {
                  ...modifiedData?.displayRecommendedData[index]
                    ?.multiSelectData?.configureData,
                  selected: false,
                  reset: false,
                },
              },
            };
          }
        }
        return modifiedData;
      });
    } else {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        let modifiedData = {
          ...prevData,
        };
        if (
          modifiedData.displayRecommendedData &&
          modifiedData.displayRecommendedData[index]
        ) {
          modifiedData.displayRecommendedData[index] = {
            ...modifiedData.displayRecommendedData[index],
            multiSelectData: {
              ...modifiedData.displayRecommendedData[index]?.multiSelectData,
              configureData: {
                ...modifiedData?.displayRecommendedData?.[index]
                  ?.multiSelectData?.configureData,
                selected: checkBoxData?.selected || false,
                reset: false,
              },
            },
          };
        }
        return modifiedData;
      });
    }
  };

  const bulkApplyInputResponseHandler = (inputResponseData: any) => {
    const statusSuccess =
      inputResponseData.status != 400 ? "success" : "failed";
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      let modifiedData = {
        ...prevData,
      };
      if (
        inputResponseData?.apiResponse?.nextActionRequired &&
        Object.keys(inputResponseData?.apiResponse?.nextActionRequired).length >
          0
      ) {
        if (!modifiedData.nudgeData)
          modifiedData.nudgeData =
            inputResponseData?.apiResponse?.nextActionRequired;

        if (!modifiedData.popUpOpened) modifiedData.popUpOpened = true;
      }
      const totalSuccessfulBulkApply = statusSuccess == "success";
      modifiedData.totalSuccessfulBulkApplyData = {
        fetchDone: statusSuccess,
        totalSuccessfulBulkApply: totalSuccessfulBulkApply
          ? modifiedData.multiSelectCount ||
            (queryParams.action === "bulkApply" && queryParams?.jobIds?.length)
          : 0,
        totalBulkApply: modifiedData.multiSelectCount,
      };

      return modifiedData;
    });
  };

  const clearBulkApplySelect = () => {
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      const savedJobsModified = {
        ...prevData,
      };
      if (savedJobsModified.firstMultiSelectIndex) {
        savedJobsModified.firstMultiSelectIndex.forEach((key: number) => {
          if (savedJobsModified.displayRecommendedData) {
            savedJobsModified.displayRecommendedData[key] = {
              ...savedJobsModified.displayRecommendedData[key],
              multiSelectData: {
                ...savedJobsModified.displayRecommendedData[key]
                  ?.multiSelectData,
                configureData: {
                  ...savedJobsModified?.displayRecommendedData[key]
                    ?.multiSelectData?.configureData,
                  reset: true,
                },
              },
            };
          }
        });
      }
      savedJobsModified.doNotOpenConsecutiveApops = false;
      savedJobsModified.multiSelectCount = 0;
      savedJobsModified.firstMultiSelectIndex = [];
      savedJobsModified.multiSelectJobIds = [];
      savedJobsModified.totalSuccessfulBulkApplyData = undefined;
      savedJobsModified.allMultiSelectSelected = false;
      savedJobsModified.clearAllMultiSelectSelected = true;
      savedJobsModified.triggeredActionSource = "clearSelection";
      savedJobsModified.resetQuickApplyFilter = true;
      return savedJobsModified;
    });
  };

  useEffect(() => {
    if (recommendedJobData?.discardBulkApplyPopup?.decision) {
      if (
        recommendedJobData.discardBulkApplyPopup?.type == "quickApplySelect"
      ) {
        handleQuickApplyFilter(
          recommendedJobData.discardBulkApplyPopup?.argumentStorage,
          "select",
        );
      } else if (
        recommendedJobData.discardBulkApplyPopup?.type == "quickApplyRemove"
      ) {
        handleQuickApplyFilter(
          recommendedJobData.discardBulkApplyPopup?.argumentStorage,
          "remove",
        );
      }
      setRecommendedJobData((prev: RecommendedPageDataType) => {
        let newData = {
          ...prev,
          triggeredActionSource: "clearQuickApplyFilter",
        };
        return newData;
      });
    }
  }, [recommendedJobData?.discardBulkApplyPopup?.decision]);

  useEffect(() => {
    if (!isMobile) {
      adjustHeight();
      window.addEventListener("resize", adjustHeight);
      return () => {
        window.removeEventListener("resize", adjustHeight);
      };
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    recommendedJobParams,
    recommendedJobData,
    isMobile,
    inputMultiSelectExists,
    toggleDashboardHeader,
  ]);

  useEffect(() => {
    if (data) {
      const newJobs = data.pages[data.pages.length - 1] || {};
      const newData = { ...recommendedJobData.data };
      let newTotalDataLength: number;
      let newTotalPagingLength: number;
      let completeLinearData: RecommendedJobAPIDataType[] = [];
      const previousSelectionByJobId = new Map<
        string,
        RecommendedJobAPIDataType["multiSelectData"]
      >();
      (recommendedJobData.displayRecommendedData || []).forEach(
        (job: RecommendedJobAPIDataType) => {
          const jobIdentity = String(job?.jobId ?? job?.id ?? "");
          if (jobIdentity && job?.multiSelectData) {
            previousSelectionByJobId.set(jobIdentity, job.multiSelectData);
          }
        },
      );
      const isPageReset = recommendedJobParams.currentPage === 1;
      if (recommendedJobParams.filterStatus) {
        newData[recommendedJobParams.filterStatus] =
          recommendedJobParams.currentPage == 1
            ? {
                ...(recommendedJobData.data?.[
                  recommendedJobParams.filterStatus
                ] ?? {}),
                ...(newJobs[
                  recommendedJobParams.filterStatus as keyof RecommendedJobAPIKeysType
                ] ?? {}),
              }
            : {
                ...(recommendedJobData.data?.[
                  recommendedJobParams.filterStatus
                ] ?? {}),
                ...(newJobs[
                  recommendedJobParams.filterStatus as keyof RecommendedJobAPIKeysType
                ] ?? {}),
                data: [
                  ...(recommendedJobData.data?.[
                    recommendedJobParams.filterStatus
                  ]?.data ?? []),
                  ...(newJobs[
                    recommendedJobParams.filterStatus as keyof RecommendedJobAPIKeysType
                  ]?.data ?? []),
                ],
              };

        if (recommendedJobData.nextFilter) {
          Object.keys(newJobs).forEach((job: string) => {
            newData[job] =
              recommendedJobParams.currentPage == 1
                ? { ...(newJobs[job as keyof RecommendedJobAPIKeysType] ?? {}) }
                : {
                    ...(newJobs[job as keyof RecommendedJobAPIKeysType] ?? {}),
                    data: [
                      ...(recommendedJobData.data?.[job]?.data ?? []),
                      ...(newJobs[job as keyof RecommendedJobAPIKeysType]
                        ?.data ?? []),
                    ],
                  };
          });
          let newDisplayRecommendedData = [
            ...(recommendedJobData?.displayRecommendedData || []),
          ];
          if (recommendedJobParams.currentPage == 1) {
            newDisplayRecommendedData = [
              ...newDisplayRecommendedData,
              ...(newData?.[recommendedJobParams.filterStatus]?.data ?? []),
            ];
          }
          completeLinearData = mergeAppended(
            recommendedJobData?.data ?? {},
            newData ?? {},
            newDisplayRecommendedData ?? [],
          );

          const { calculateTotalDataLength, calculateTotalPagingLength } =
            Object.keys(newData).reduce(
              (acc, key) => {
                const dataLength = newData[key]?.data?.length || 0;
                const pagingTotal =
                  newData[key]?.meta?.paging?.total || dataLength;

                acc.calculateTotalDataLength += dataLength;
                acc.calculateTotalPagingLength += pagingTotal;

                return acc;
              },
              { calculateTotalDataLength: 0, calculateTotalPagingLength: 0 },
            );
          newTotalDataLength = calculateTotalDataLength;
          newTotalPagingLength = calculateTotalPagingLength;
        } else {
          newTotalDataLength =
            newData[recommendedJobParams.filterStatus]?.data?.length || 0;
          newTotalPagingLength =
            newData[recommendedJobParams.filterStatus]?.meta?.paging?.total ||
            newData[recommendedJobParams.filterStatus]?.data?.length ||
            0;
          completeLinearData = [
            ...(newData[recommendedJobParams.filterStatus]?.data || []),
          ];
        }
      }
      completeLinearData =
        completeLinearData?.map((job: RecommendedJobAPIDataType) => {
          const jobIdentity = String(job?.jobId ?? job?.id ?? "");
          const previousMultiSelectData =
            previousSelectionByJobId.get(jobIdentity);
          let modifiedData = {
            ...job,
          };
          if (!modifiedData.multiSelectData && previousMultiSelectData) {
            modifiedData.multiSelectData = {
              ...previousMultiSelectData,
              configureData: {
                fetch: false,
                selected:
                  previousMultiSelectData?.configureData?.selected || false,
                reset: false,
                isJobApplied:
                  previousMultiSelectData?.configureData?.isJobApplied,
              },
            };
          }
          if (!modifiedData.multiSelectData) {
            modifiedData.multiSelectData = {
              configureData: {
                fetch: false,
                selected: false,
                reset: false,
              },
            };
          }
          if (
            (recommendedJobParams.filterStatus &&
              recommendedJobParams.currentPage == 1) ||
            recommendedJobParams.currentPage == 1
          ) {
            modifiedData.multiSelectData = {
              configureData: {
                fetch: false,
                selected: false,
                reset: true,
              },
            };
          }

          return modifiedData;
        }) || [];
      const preservedSelectedIndexes: number[] = [];
      const preservedSelectedJobIds: number[] = [];
      completeLinearData.forEach(
        (job: RecommendedJobAPIDataType, idx: number) => {
          if (job?.multiSelectData?.configureData?.selected) {
            preservedSelectedIndexes.push(idx);
            const selectedJobId = Number(job?.jobId);
            if (!Number.isNaN(selectedJobId)) {
              preservedSelectedJobIds.push(selectedJobId);
            }
          }
        },
      );
      jobUserInfoHandler(completeLinearData);
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const recommendedJobsModified = {
          ...prevData,
          data: newData,
          totalDataLength: newTotalDataLength,
          totalPagingLength: newTotalPagingLength,
          displayRecommendedData: completeLinearData,
        };
        const shouldResetPreviousSelectedCards =
          isPageReset ||
          (recommendedJobsModified.triggeredActionSource === "selectAll" &&
            recommendedJobsModified.quickApplyFilter) ||
          recommendedJobsModified.triggeredActionSource === "clearSelection" ||
          recommendedJobsModified.triggeredActionSource === "applySuccess";
        if (
          shouldResetPreviousSelectedCards &&
          ((recommendedJobParams.filterStatus &&
            recommendedJobParams.currentPage !== 1) ||
            recommendedJobParams.currentPage !== 1 ||
            recommendedJobData.nextFilter)
        ) {
          recommendedJobsModified?.firstMultiSelectIndex?.forEach(
            (key: number) => {
              recommendedJobsModified.displayRecommendedData[key] = {
                ...(recommendedJobsModified?.displayRecommendedData?.[key] ||
                  {}),
                multiSelectData: {
                  ...(recommendedJobsModified?.displayRecommendedData[key]
                    ?.multiSelectData || {}),
                  configureData: {
                    ...(recommendedJobsModified?.displayRecommendedData?.[key]
                      ?.multiSelectData?.configureData || {}),
                    reset: true,
                  },
                },
              };
            },
          );
        }

        if (
          recommendedJobsModified.triggeredActionSource === "selectAll" &&
          recommendedJobsModified.quickApplyFilter
        ) {
          recommendedJobsModified.selectAllInitiation = "start";
        } else if (!isPageReset) {
          recommendedJobsModified.doNotOpenConsecutiveApops = false;
          recommendedJobsModified.multiSelectCount =
            preservedSelectedIndexes.length;
          recommendedJobsModified.firstMultiSelectIndex =
            preservedSelectedIndexes;
          recommendedJobsModified.multiSelectJobIds = preservedSelectedJobIds;
          recommendedJobsModified.firstMultiSelectIndex.forEach(
            (selectedIdx: number) => {
              if (
                recommendedJobsModified.displayRecommendedData?.[selectedIdx]
              ) {
                recommendedJobsModified.displayRecommendedData[selectedIdx] = {
                  ...recommendedJobsModified.displayRecommendedData[
                    selectedIdx
                  ],
                  multiSelectData: {
                    ...recommendedJobsModified.displayRecommendedData[
                      selectedIdx
                    ]?.multiSelectData,
                    configureData: {
                      ...recommendedJobsModified.displayRecommendedData[
                        selectedIdx
                      ]?.multiSelectData?.configureData,
                      fetch: false,
                      selected: true,
                      reset: false,
                    },
                  },
                };
              }
            },
          );
          recommendedJobsModified.totalSuccessfulBulkApplyData = undefined;
          recommendedJobsModified.allMultiSelectSelected =
            recommendedJobsModified.retainAllMultiSelectSelected || false;
          recommendedJobsModified.clearAllMultiSelectSelected = false;
        } else {
          recommendedJobsModified.doNotOpenConsecutiveApops = false;
          recommendedJobsModified.multiSelectCount = 0;
          recommendedJobsModified.firstMultiSelectIndex = [];
          recommendedJobsModified.multiSelectJobIds = [];
          recommendedJobsModified.totalSuccessfulBulkApplyData = undefined;
          recommendedJobsModified.allMultiSelectSelected = false;
          recommendedJobsModified.clearAllMultiSelectSelected = true;
        }
        if (recommendedJobsModified.nextFilter) {
          recommendedJobsModified.stopFilterCalls = true;
          recommendedJobsModified.nextFilter = false;
        }
        recommendedJobsModified.resetQuickApplyFilter = true;
        if (!prevData?.autoApplyData?.enabled)
          recommendedJobsModified["autoApplyData"] = {};
        recommendedJobsModified["filterDisable"] = false;
        return recommendedJobsModified;
      });
    } else if (isError) {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const recommendedJobsModified = {
          ...prevData,
          data: { ...prevData.data },
        };
        if (!prevData?.autoApplyData?.enabled)
          recommendedJobsModified["autoApplyData"] = {};
        recommendedJobsModified["filterDisable"] = false;
        recommendedJobsModified["nextFilter"] = false;
        return recommendedJobsModified;
      });
    }
  }, [data, isError]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      if (isFetchingNextPage) {
        container.classList.remove("overflow-y-scroll");
        container.classList.add("overflow-y-hidden");
      } else {
        setTimeout(() => {
          container.classList.remove("overflow-y-hidden");
          container.classList.add("overflow-y-scroll");
        }, 200);
      }
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (recommendedJobParams.initialPage === 1) {
      refetch();
    }
  }, [recommendedJobParams.resetType]);

  useEffect(() => {
    if (
      recommendedJobData?.totalSuccessfulBulkApplyData?.fetchDone &&
      !recommendedJobData?.popUpOpened
    ) {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
        };
        savedJobsModified["toastMessage"] = {
          text:
            recommendedJobData?.totalSuccessfulBulkApplyData?.fetchDone ===
            "success"
              ? `Job application successful for ${recommendedJobData?.totalSuccessfulBulkApplyData?.totalSuccessfulBulkApply || 0} out of ${recommendedJobData.multiSelectCount} jobs`
              : "Bulk Apply failed. Your profile is missing some key details employers need. Update now to continue.",
          type:
            recommendedJobData?.totalSuccessfulBulkApplyData?.fetchDone ===
            "success"
              ? "success"
              : "error",
          show: true,
        };
        savedJobsModified?.firstMultiSelectIndex?.forEach((key: number) => {
          if (
            recommendedJobData?.totalSuccessfulBulkApplyData?.fetchDone ===
            "success"
          ) {
            if (savedJobsModified.displayRecommendedData)
              savedJobsModified.displayRecommendedData[key] = {
                ...savedJobsModified?.displayRecommendedData?.[key],
                isApplied: true,
                multiSelectData: {
                  ...savedJobsModified?.displayRecommendedData?.[key]
                    .multiSelectData,
                  configureData: {
                    ...savedJobsModified?.displayRecommendedData?.[key]
                      ?.multiSelectData?.configureData,
                    fetch: false,
                    isJobApplied: true,
                  },
                },
              };
          }
          if (
            recommendedJobData?.totalSuccessfulBulkApplyData?.fetchDone ===
            "failed"
          ) {
            if (savedJobsModified.displayRecommendedData)
              savedJobsModified.displayRecommendedData[key] = {
                ...savedJobsModified?.displayRecommendedData?.[key],
                multiSelectData: {
                  ...savedJobsModified?.displayRecommendedData?.[key]
                    .multiSelectData,
                  configureData: {
                    ...savedJobsModified?.displayRecommendedData?.[key]
                      ?.multiSelectData?.configureData,
                    reset: true,
                    fetch: false,
                  },
                },
              };
          }
        });
        savedJobsModified.popUpOpened = undefined;
        savedJobsModified.doNotOpenConsecutiveApops = false;
        savedJobsModified.multiSelectCount = 0;
        savedJobsModified.firstMultiSelectIndex = [];
        savedJobsModified.multiSelectJobIds = [];
        savedJobsModified.totalSuccessfulBulkApplyData = undefined;
        savedJobsModified.discardBulkApplyPopup = undefined;
        savedJobsModified.allMultiSelectSelected = false;
        savedJobsModified.clearAllMultiSelectSelected = true;
        savedJobsModified.triggeredActionSource = "applySuccess";
        return savedJobsModified;
      });
    }
  }, [
    recommendedJobData.totalSuccessfulBulkApplyData,
    recommendedJobData?.popUpOpened,
  ]);

  useEffect(() => {
    if (
      (recommendedJobData.selectedFilter &&
        Array.isArray(recommendedJobData.selectedFilter) &&
        recommendedJobData.selectFilterFetch === "client") ||
      recommendedJobData.quickApplyFilter
    ) {
      if (
        !recommendedJobData.nextFilter &&
        !recommendedJobData.stopFilterCalls
      ) {
        if (isMobile) {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        } else {
          const container = containerRef.current;
          if (container) {
            container.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }
        }
      }
      if (!recommendedJobData.stopFilterCalls) {
        resetAndRefetch();
      }
    }
  }, [
    recommendedJobData.selectedFilter,
    recommendedJobData.quickApplyFilter,
    isMobile,
  ]);

  useEffect(() => {
    if (
      recommendedJobData.isLoadingJobDesc &&
      !recommendedJobParams.loadingJobCard &&
      recommendedJobData.abTestBulkApply
    ) {
      setTimeout(() => {
        setRecommendedJobData((prevData: RecommendedPageDataType) => {
          const modifiedData = { ...prevData, isLoadingJobDesc: false };
          if (!prevData.saveUnsaveJobHandler)
            modifiedData.saveUnsaveJobHandler = saveUnsaveJobHandler;
          if (!prevData.leftSideRef) modifiedData.leftSideRef = containerRef;
          return modifiedData;
        });
      }, 200);
      if (recommendedJobData.stopFilterCalls) {
        handlePaginationScroll();
      }
    } else if (
      !recommendedJobData.isLoadingJobDesc &&
      !recommendedJobParams.loadingJobCard &&
      recommendedJobData.abTestBulkApply &&
      !recommendedJobData.noScroll
    ) {
      handlePaginationScroll();
    }
  }, [
    recommendedJobParams.loadingJobCard,
    recommendedJobData?.abTestBulkApply,
  ]);

  useEffect(() => {
    if (
      recommendedJobParams.loadingJobCard &&
      !recommendedJobData.newDataLoader
    ) {
      setTimeout(() => {
        setRecommendedJobParams((prev) => ({
          ...prev,
          loadingJobCard: false,
        }));
      }, 200);
    } else if (!recommendedJobData.newDataLoader) {
      setTimeout(() => {
        setRecommendedJobData((prevData: RecommendedPageDataType) => {
          return { ...prevData, isLoadingJobDesc: false };
        });
      }, 200);
    }
  }, [recommendedJobData.jobDescIndex, recommendedJobData.data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localStorage.getItem("multiSelectJobsFirstVisit") !== "true") {
        setTimeout(() => {
          tooltipBtnRef?.current?.click();
        }, 100);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    let stableTimer: NodeJS.Timeout | null = null;

    const observer = new MutationObserver(() => {
      const matches = Array.from(
        containerRef.current!.querySelectorAll<HTMLElement>(
          "#quick-apply-check",
        ),
      );

      const validMatches = matches.filter(
        (el) => !el.closest("#select-all-checkbox"),
      );

      const target = validMatches.length > 0 ? validMatches[0] : null;

      if (target) {
        if (stableTimer) clearTimeout(stableTimer);
        stableTimer = setTimeout(() => {
          const wrapper = target.closest<HTMLElement>("[data-index]");
          if (wrapper) {
            const idx = Number(wrapper.dataset.index);
            setInputMultiSelectExists(idx >= 0);
            observer.disconnect();
          }
        }, 1000);
      } else {
        if (stableTimer) clearTimeout(stableTimer);
        setInputMultiSelectExists(null);
      }
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      if (stableTimer) clearTimeout(stableTimer);
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    if (!containerRef.current) return;

    let mo: MutationObserver | null = null;
    let cleanup: (() => void) | null = null;

    const setupPin = (bar: HTMLElement) => {
      const container = containerRef.current!;
      const headerElement = document.getElementById("dashboard-header");

      const computeThreshold = () => {
        const barRect = bar.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        return container.scrollTop + (barRect.top - containerRect.top) + 20;
      };

      let threshold = computeThreshold();
      let pinned = false;

      const onScroll = () => {
        const scrollTop = container.scrollTop;
        const profileVisibilityNudgeElem = document.getElementById(
          "profile-visibility-nudge",
        );
        const topHeaderBannerHeight =
          document.querySelector(".rec_top_panel_container")?.clientHeight ?? 0;

        if (scrollTop >= threshold && !pinned) {
          if (headerElement) {
            headerElement.style.visibility = "hidden";
            headerElement.style.setProperty(
              "margin-bottom",
              `0px`,
              "important",
            );

            if (profileVisibilityNudgeElem) {
              profileVisibilityNudgeElem.style.display = "none";
            }
          }
          if (topHeaderBannerHeight) {
            bar.classList.add(
              "pinned-in-header-desktop",
              "pinned-in-header-desktop-with-banner",
            );
          } else {
            bar.classList.add("pinned-in-header-desktop");
          }
          pinned = true;
          setToggleDashboardHeader(true);
        } else if (scrollTop < threshold && pinned) {
          if (headerElement) {
            headerElement.style.visibility = "visible";
            headerElement.style.setProperty(
              "margin-bottom",
              `24px`,
              "important",
            );

            if (profileVisibilityNudgeElem) {
              profileVisibilityNudgeElem.style.display = "flex";
            }
          }
          if (topHeaderBannerHeight) {
            bar.classList.remove(
              "pinned-in-header-desktop",
              "pinned-in-header-desktop-with-banner",
            );
          } else {
            bar.classList.remove("pinned-in-header-desktop");
          }
          pinned = false;
          setToggleDashboardHeader(false);
        }
      };

      const onResize = () => {
        threshold = computeThreshold();
        if (pinned) onScroll();
      };

      container.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize);

      cleanup = () => {
        container.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
      };
    };

    const bar = document.getElementById("select-all-checkbox");
    if (bar) {
      setupPin(bar);
    } else {
      mo = new MutationObserver((_, obs) => {
        const el = document.getElementById("select-all-checkbox");
        if (el) {
          obs.disconnect();
          setupPin(el);
        }
      });
      mo.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (mo) mo.disconnect();
      if (cleanup) cleanup();
    };
  }, [isMobile, containerRef.current]);

  useEffect(() => {
    if (!isMobile) return;
    if (!containerRef.current) return;

    let mo: MutationObserver | null = null;
    let cleanupScroll: (() => void) | null = null;
    const initPin = (bar: HTMLElement, container: HTMLElement) => {
      const { top: barTop, left: barLeft } = bar.getBoundingClientRect();
      const threshold = window.scrollY + barTop;

      let pinned = false;
      const onScroll = () => {
        if (window.scrollY >= threshold && !pinned) {
          bar.classList.add("pinned-in-header");
          pinned = true;
          setToggleDashboardHeader(true);
        } else if (window.scrollY < threshold && pinned) {
          bar.classList.remove("pinned-in-header");
          pinned = false;
          setToggleDashboardHeader(false);
        }
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    };
    const bar = document.getElementById("select-all-checkbox");
    const container = containerRef.current!;
    if (bar && container) {
      cleanupScroll = initPin(bar, container);
    } else {
      mo = new MutationObserver((_, obs) => {
        const el = document.getElementById("select-all-checkbox");
        const container = containerRef.current!;
        if (el && container) {
          obs.disconnect();
          cleanupScroll = initPin(el, container);
        }
      });
      mo.observe(document.body, { subtree: true, childList: true });
    }

    return () => {
      if (mo) mo.disconnect();
      if (cleanupScroll) cleanupScroll();
    };
  }, [isMobile, containerRef.current]);

  useEffect(() => {
    if (isMobile) return;
    const container = containerRef.current;
    const parentContainer = document.getElementById("dashboard-page");
    if (!parentContainer) return;

    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (maxScroll <= 0) return;

      e.preventDefault();
      let next = container.scrollTop + e.deltaY;
      if (next < 0) next = 0;
      if (next > maxScroll) next = maxScroll;
      container.scrollTop = next;
    };

    parentContainer.addEventListener("wheel", onWheel, { passive: false });
    return () => parentContainer.removeEventListener("wheel", onWheel);
  }, [containerRef, isMobile]);

  const handleToolTipStorage = () => {
    localStorage.setItem("multiSelectJobsFirstVisit", "true");
  };

  const toolTipContent = (
    <div className="!my-1 !flex !flex-col !gap-[10px]">
      <p className="text-[#FFF)] h-max max-w-[200px] text-sm font-normal leading-[22px]">
        {inputMultiSelectExists
          ? "Use checkbox to multi select the jobs to apply."
          : "No quick apply jobs available for this search"}
      </p>
      <div className="!flex !justify-end">
        <Button
          label={"Got it!"}
          modifier="tertiary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            tooltipBtnRef?.current?.click();
            handleToolTipStorage();
          }}
          size="xs"
          url=""
          className="toolTipBtn bg-white"
        />
      </div>
    </div>
  );

  const sectionTitle = (index: number) => {
    return index === 0 &&
      recommendedJobData?.data?.["preference"]?.data?.length > 0 ? (
      <div
        className="!pb-3 !pl-4 text-[14px] font-medium not-italic leading-[20px] text-[#17142A]"
        id="jobs-based-on-preferences"
      >
        Jobs based on preferences
      </div>
    ) : index ===
      (recommendedJobData?.data?.["preference"]?.data?.length || 0) ? (
      <div
        className="!pb-3 !pl-4 text-[14px] font-medium not-italic leading-[20px] text-[#17142A]"
        id="jobs-based-on-applied-history"
      >
        Jobs based on applied history
      </div>
    ) : null;
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const jobIdsParam = params.get("jobIds");
    const actionParam = params.get("action");

    const jobIds = jobIdsParam
      ? jobIdsParam
          .split(",")
          .map((val: string) => Number(val.trim()))
          .filter((val: number) => !Number.isNaN(val))
      : [];

    setQueryParams({
      jobIds,
      action: actionParam || undefined,
    });

    if (actionParam === "bulkApply" && jobIds) {
      setRecommendedJobData((prev: RecommendedPageDataType) => ({
        ...prev,
        multiSelectJobIds: jobIds,
        multiSelectCount: jobIds.length,
      }));
    }
  }, []);

  useEffect(() => {
    const { action, jobIds } = queryParams;
    if (action !== "bulkApply" || jobIds?.length === 0) return;

    const tryClickButton = (container: HTMLElement): boolean => {
      const btn = container.querySelector<HTMLButtonElement>("button");
      if (btn) {
        btn.click();
        return true;
      }
      return false;
    };

    const tryImmediateClick = () => {
      const container = bulkApplyBtnContainerRef.current;
      if (container && tryClickButton(container)) {
        return true;
      }
      return false;
    };

    if (tryImmediateClick()) return;

    const observer = new MutationObserver(() => {
      if (tryImmediateClick()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [queryParams]);

  useEffect(() => {
    if (
      recommendedJobData?.selectAllInitiation === "start" &&
      !recommendedJobData?.isLoadingJobDesc
    ) {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
        };
        if (!savedJobsModified?.clearAllMultiSelectSelected) {
          savedJobsModified?.firstMultiSelectIndex?.forEach((key: number) => {
            if (savedJobsModified.displayRecommendedData) {
              savedJobsModified.displayRecommendedData[key] = {
                ...savedJobsModified.displayRecommendedData[key],
                multiSelectData: {
                  ...savedJobsModified.displayRecommendedData[key]
                    ?.multiSelectData,
                  configureData: {
                    ...savedJobsModified?.displayRecommendedData?.[key]
                      ?.multiSelectData?.configureData,
                    reset: true,
                  },
                },
              };
            }
          });
          savedJobsModified.doNotOpenConsecutiveApops = false;
          savedJobsModified.multiSelectCount = 0;
          savedJobsModified.firstMultiSelectIndex = [];
          savedJobsModified.multiSelectJobIds = [];
          savedJobsModified.totalSuccessfulBulkApplyData = undefined;
        }
        return savedJobsModified;
      });

      setTimeout(() => {
        setRecommendedJobData((prevData: RecommendedPageDataType) => {
          const savedJobsModified = {
            ...prevData,
          };

          if (!savedJobsModified?.clearAllMultiSelectSelected) {
            savedJobsModified.allMultiSelectSelected = true;
            savedJobsModified.retainAllMultiSelectSelected = true;
            setEnableSelectAll(false);
          }
          savedJobsModified.triggeredActionSource = undefined;
          savedJobsModified.selectAllInitiation = undefined;
          return savedJobsModified;
        });
      }, 100);
    }
  }, [
    recommendedJobData?.selectAllInitiation,
    recommendedJobData.isLoadingJobDesc,
  ]);

  useEffect(() => {
    const el = (
      isMobile ? document.scrollingElement : containerRef.current
    ) as HTMLElement | null;
    if (!el) return;
    if (!recommendedJobData?.displayRecommendedData?.length) return;

    let rafId = 0;
    let skippedInitial = false;

    const runCheck = () => {
      if (!skippedInitial) {
        skippedInitial = true;
        return;
      }

      const threshold = 2;
      const canScroll = el.scrollHeight - el.clientHeight > threshold;

      if (!canScroll && recommendedJobParams.filterStatus === "preference") {
        setRecommendedJobData((prev: RecommendedPageDataType) => {
          if (prev.nextFilter === true) return prev;
          return { ...prev, nextFilter: true, noScroll: true };
        });
      }
    };

    const scheduleCheck = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(runCheck);
    };

    window.addEventListener("resize", scheduleCheck);

    const observer = new MutationObserver(scheduleCheck);
    observer.observe(el, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("resize", scheduleCheck);
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="scrollbar-hide recommended-jobs-container flex flex-col pb-3"
      onScroll={handleScroll}
      id="recommended-jobs-container"
    >
      {!recommendedJobParams.loadingJobCard &&
        !recommendedJobData.filterDisable &&
        recommendedJobData.abTestBulkApply && (
          <div className={`${isMobile ? "!mb-4" : "!mb-3"}`}>
            <p className="!ml-[20px] overflow-hidden truncate text-xs font-normal leading-4 text-[#59566C] md:!ml-[15px]">
              Showing{" "}
              <span className="text-xs font-medium text-[#17142A]">
                {showPagingLength} {showPagingLength > 1 ? "jobs" : "job"}
              </span>
            </p>
          </div>
        )}
      {multiSelectFeature && recommendedJobData.abTestBulkApply == "success" ? (
        <div
          className={`!mb-4 flex flex-row items-center ${isMobile ? "justify-start gap-[38px] pl-4" : "justify-center gap-[89px]"}`}
          id="select-all-checkbox"
          ref={selectAllRef}
        >
          <div
            className={`!m-0 flex gap-2 !p-0 md:!mb-[0px] ${!isMobile ? "w-[227px]" : "w-[173px]"}`}
          >
            <CheckboxList
              className={`relative bottom-[4px] text-sm font-normal leading-5 text-[#17142A]`}
              defaultChecked={false}
              errorLabel="Something went wrong"
              reset={recommendedJobData?.clearAllMultiSelectSelected || false}
              disabled={!inputMultiSelectExists}
              eventChangeHandler={(data: selectAllEventType) => {
                eventChangeHandlerForSelectAll(data);
              }}
              label=""
              layout="row"
              options={[
                {
                  displayName: "",
                  id: "quick-apply-check",
                  value: "quick-apply-check",
                },
              ]}
              enableAutoSelect={enableSelectAll}
              size="large"
              allChecked={
                Number(bulkApplyMultiSelect) ===
                Number(recommendedJobData.multiSelectCount)
              }
            />
            <div
              className={`flex justify-center gap-1 ${isMobile ? "flex-col items-start" : "items-center"}`}
              ref={selectAllTextRef}
            >
              <p className="text-[12px] font-medium leading-[16px] text-[#17142A]">
                {recommendedJobData.multiSelectCount > 0 &&
                recommendedJobData.allMultiSelectSelected
                  ? `${recommendedJobData.multiSelectCount} Selected`
                  : "Select all"}
              </p>
              {!recommendedJobData.allMultiSelectSelected && (
                <p className="!m-0 !mb-0 !p-0 text-[12px] font-normal leading-[16px] text-[#A5A3AE]">
                  (upto {bulkApplyMultiSelect || 5} quick apply jobs)
                </p>
              )}
            </div>
          </div>
          <div className="relative bottom-[0px] right-[326px] h-0">
            <Tooltip
              content={toolTipContent}
              icon={
                <button className="toolTipIconBtn" ref={tooltipBtnRef}></button>
              }
              placement={!isMobile ? "bottom" : "top-right"}
              calculatePlacement={true}
              tooltipBeakClassName="md:!right-[-94px] !bottom-[98%] !top-auto transform rotate-180 md:!rotate-0 inline-block md:!block"
              tooltipExtraClassName={`md:!left-[115px] top-[8px] md:!top-[14px] !left-[110px] desktop-sticky-class`}
              stopOutSideClick={true}
            />
          </div>
          <div className="bg-surface-primary-disabled invisible flex h-6 w-fit max-w-[117px] items-center justify-center gap-1 rounded-xl py-0.5 pl-2 pr-1 text-xs">
            <span className="text-start font-medium">Quick Apply</span>
            <Toggle
              toggleSize="mini"
              checked={recommendedJobData.quickApplyFilter}
              onChange={onHandleQuickApplyFilter}
              isbgGreen={true}
            />
          </div>
        </div>
      ) : null}
      {!recommendedJobParams.loadingJobCard &&
        !recommendedJobData.filterDisable &&
        recommendedJobData.abTestBulkApply &&
        recommendedJobData?.displayRecommendedData?.map(
          (data: RecommendedJobAPIDataType, index: number) => {
            let fullName;
            if (index == 3) {
              fullName =
                recommendedJobData?.profilePersonalDetails
                  ?.personalDetailSection?.personalDetails?.fullName || "";
            }
            return (
              <div
                data-index={index}
                id={`job-card-${index}`}
                key={data?.companyId || index}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={`${isMobile ? "mb-3" : recommendedJobData.multiSelectCount > 0 && recommendedJobData?.displayRecommendedData?.length - 1 === index ? "!mb-[90px]" : "!mb-5"}`}
              >
                {sectionTitle(index)}
                {index == 3 ? (
                  <a
                    href="/career-services/career-booster"
                    className="cursor-pointer"
                    target="_blank"
                  >
                    <MessageCard
                      key={index}
                      bodyText={`${fullName ? `Hi ${fullName?.split(" ")[0]}, revamp ` : "Revamp "}your resume, plus rank higher to get more call-backs.`}
                      className="bg-message-card mb-[16px]"
                      iconAltText="Rocket Nudge"
                      leftIconSrc={`https://${API_ENDPOINT("").media.rocketNudge}`}
                    />
                  </a>
                ) : null}
                {index == 4 &&
                !recommendedJobData?.outreachSubscriptionData?.data
                  ?.global_consent?.subscribed ? (
                  <div className="mb-5 flex items-start gap-3 rounded-[16px] border border-[rgba(115,215,195,0.5)] bg-[#F1FBF9] p-4 shadow-[0_10px_45px_0_rgba(0,0,0,0.03)]">
                    <div className="relative bottom-[6px] text-[28px] font-bold leading-[40px] text-[#17142A]">
                      😬
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-[6px]">
                        <p className="text-sm font-bold leading-5 text-[#17142A]">
                          You are missing out on job offers!
                        </p>
                        <p className="text-sm font-normal leading-5 text-[#17142A]">
                          Let recruiters reach out to you with jobs that match
                          your profile.
                        </p>
                      </div>
                      <Button
                        label="Yes, I am interested"
                        modifier="primary-black"
                        onClick={() => {
                          window.location.href =
                            "/seeker/settings?activeTab=email-and-notification";
                        }}
                        size="lg"
                        url=""
                        className="!w-[137px] !px-3 !py-2 text-center text-xs font-normal leading-4 !text-[#FFF]"
                      />
                    </div>
                  </div>
                ) : null}
                {index == 8 ? (
                  <CareerServiceBanners
                    pageName="Dashboard Recommendation"
                    imgBasePath={`https://${mediaUrl}/trex/search/public/`}
                    handleEventTracking={handleEventTracking}
                  />
                ) : null}
                <div>
                  <JobCard
                    key={data?.companyId || index}
                    cardClickHandler={(url: string) => {
                      cardClickHandler(url, index);
                    }}
                    isSyndicatedQuickApplyEnabled={isSyndicateQuickApplyEnabled}
                    isMobile={isMobile}
                    jobData={{
                      ...data,
                      jdUrl: getJDUrlWithDomain(data.jdUrl, host),
                    }}
                    saveUnsaveJobHandler={() => {
                      saveUnsaveJobHandler(data?.jobId, "apiCall", {
                        callBackSuccess: false,
                      });
                    }}
                    updateIsSaved={recommendedJobData.savedJobs.includes(
                      data?.jobId,
                    )}
                    applyBtnComponent={
                      <ApplyJob
                        key={data?.companyId || index}
                        jobData={data}
                        siteProperties={recommendedJobData.sitePropsProperties}
                        isSyndicateQuickApplyEnabled={
                          isSyndicateQuickApplyEnabled
                        }
                        pageName="Dashboard"
                        btnClass="rounded-full bg-surface-primary-pressed !font-semibold border-0 text-xs text-content-strong py-2 px-3"
                        isLoggedIn={true}
                        isMobile={isMobile}
                        divClass="w-1/2 md:!w-fit"
                        isJobApplied={
                          data?.isApplied ||
                          recommendedJobData?.userInfo?.appliedJobIds?.includes(
                            data?.id || "",
                          ) ||
                          false
                        }
                        inputSelection={false}
                        inputResponse={(inputData: any) => {
                          if (inputData.status === 200) {
                            updateJobApplicationState(
                              data.id ?? "",
                              true,
                              index,
                            );
                          }
                        }}
                        originPage={"dashboard"}
                      />
                    }
                    multiSelectBtnComponent={
                      multiSelectFeature &&
                      recommendedJobData.abTestBulkApply == "success" ? (
                        <div>
                          <ApplyJob
                            key={data?.companyId || index}
                            jobData={data}
                            siteProperties={
                              recommendedJobData.sitePropsProperties
                            }
                            isSyndicateQuickApplyEnabled={
                              isSyndicateQuickApplyEnabled
                            }
                            pageName={"Dashboard"}
                            btnClass={
                              "!h-auto md:!h-auto !px-2 !py-[6px] !text-[12px]"
                            }
                            inputHandler={(checkBoxData: checkBoxInputType) => {
                              bulkApplyInputHandler(checkBoxData, index, data);
                            }}
                            isLoggedIn={true}
                            isMobile={isMobile}
                            applyBtnType="checkbox"
                            applyBtnConfigure={
                              data.multiSelectData?.configureData || {
                                fetch: false,
                                selected: false,
                                reset: false,
                                isJobApplied: false,
                              }
                            }
                            doNotOpenConsecutiveApops={
                              recommendedJobData.doNotOpenConsecutiveApops
                            }
                            isJobApplied={
                              data?.isApplied ||
                              recommendedJobData?.userInfo?.appliedJobIds?.includes(
                                data?.id || "",
                              ) ||
                              recommendedJobData?.displayRecommendedData?.[
                                index
                              ]?.multiSelectData?.configureData?.isJobApplied ||
                              false
                            }
                            inputSelection={
                              recommendedJobData?.allMultiSelectSelected ||
                              false
                            }
                            originPage={"dashboard"}
                          />
                        </div>
                      ) : null
                    }
                    disableCompanyNameLink={true}
                    pageName="Jobs"
                    siteContext={data?.siteContext}
                    className="hover:shadow-job-card-hover border-jobCardBorder bg-surface-primary-normal shadow-job-card relative w-auto cursor-pointer border border-solid"
                    fullView={true}
                    btnStyleProps="!hover:bg-[#EEEEEE] !bg-surface-primary-disabled"
                  />
                </div>
              </div>
            );
          },
        )}

      {Array(recommendedJobData.totalDataLength || 10)
        .fill(0)
        .map((_, index) => {
          return (
            <div
              className={`${recommendedJobParams.loadingJobCard || recommendedJobData.filterDisable || !recommendedJobData.abTestBulkApply ? "" : "hidden"} ${isMobile ? "mb-3" : "mb-5"}`}
              key={Math.random() * index}
            >
              <Skeleton />
            </div>
          );
        })}

      {recommendedJobData.multiSelectCount &&
      recommendedJobData.multiSelectCount > 0 ? (
        <div
          ref={multiSelectBtnRef}
          className="fixed bottom-0 z-[1001] !-ml-4 !-mr-4 flex w-full justify-center gap-[8px] bg-[var(--Surface-Primary-Normal,#FFF)] bg-white px-3 py-3 shadow-[0px_-4px_8px_-2px_rgba(0,0,0,0.08),_0px_-2px_2px_-2px_rgba(0,0,0,0.15)] md:!left-0 md:!-ml-0 md:!-mr-0 md:!w-[100vw] md:!flex-row md:!gap-[10px] md:!px-0 md:!py-6"
        >
          <Button
            label={"Clear Selection"}
            modifier={"tertiary"}
            onClick={clearBulkApplySelect}
            size="lg"
            url=""
            className="!h-[44px] !w-full md:!w-[170px]"
          />
          <div
            className="!h-[44px] !w-full md:!w-[170px]"
            ref={bulkApplyBtnContainerRef}
          >
            <ApplyJob
              key={"bulkApplyBtn"}
              jobData={{} as RecommendedJobAPIDataType}
              siteProperties={recommendedJobData.sitePropsProperties}
              isSyndicateQuickApplyEnabled={isSyndicateQuickApplyEnabled}
              pageName={"Dashboard"}
              btnClass={"!h-auto md:!h-auto !px-2 !py-[6px] !text-[12px]"}
              inputResponse={(inputResponseData: any) => {
                bulkApplyInputResponseHandler(inputResponseData);
              }}
              isLoggedIn={true}
              isMobile={isMobile}
              applyBtnType="bulkapply"
              doNotOpenConsecutiveApops={true}
              inputSelection={
                recommendedJobData?.allMultiSelectSelected || false
              }
              bulkApplyBtnData={{
                label: `Quick Apply (${recommendedJobData.multiSelectCount})`,
                modifier: "primary",
                url: "",
                className: "!h-[44px] !w-full",
                size: "lg",
                jobIds: recommendedJobData?.multiSelectJobIds || [],
              }}
              originPage={"dashboard"}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(RecommendedJobCard);
